import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_APS,
  RECEIVE_BATCHES,
  RECEIVE_BATCH_METADATA,
  RECEIVE_ADDRESSES,
  RECEIVE_STATES,
  RECEIVE_VENDORS,
  RECEIVE_VENDOR_METADATA,
  RECEIVE_ALL_APS,
  RECEIVE_AP_METADATA,
  RECEIVE_AP_FAILURE,
  INCREMENT_AP_COUNT,
  RECEIVE_AP_LINES,
  RECEIVE_ALL_AP_LINES,
  RECEIVING_AP_LINES,
  RECEIVE_AP_LINES_METADATA,
  RECEIVE_AP_LINES_FAILURE,
  SAVING_AP_LINES,
  SAVING_AP_LINES_FAILURE,
  UPDATING_AP_LINES,
  UPDATING_AP_LINES_FAILURE,
  CLEAR_AP_STATE,
  CLEAR_STATE,
  RESET_AP_META,
  UPDATE_CURRENT_AP_PAGE,
  DELETE_AP_LINES,
  CLEAR_AP_TRX_LINES,
  CLEAR_FILTER_AP_QUERY,
  FILTER_AP_QUERY
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import { fetchLinesNew, getTrxIds } from '@/store/modules/helpers/actionHelpers'
import {
  receiveData,
  increment,
  receiveMetadata,
  resetMetadata,
  receiveLinesMetadata,
  clearState,
  deleteLine,
  clearTrxLines
} from '@/store/modules/helpers/mutationHelpers'

const apSchema = new schema.Entity('aps')
const apListSchema = new schema.Array(apSchema)

const lineSchema = new schema.Entity('lines')
const lineListSchema = new schema.Array(lineSchema)

const batchSchema = new schema.Entity('batches')
const batchListSchema = new schema.Array(batchSchema)

const addressSchema = new schema.Entity('addresses')
const stateSchema = new schema.Entity('states')
const vendorSchema = new schema.Entity('vendors', {
  address_billing: addressSchema,
  address_contact: addressSchema,
  address_mailing: addressSchema,
  address_selected: addressSchema,
  work_state: stateSchema
})
const vendorListSchema = new schema.Array(vendorSchema)

const state = {
  data: {},
  all: [],
  allLines: [],
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  receiveDataFailure: false,
  lines: {},
  lineCount: null,
  nextLineUrl: null,
  prevLineUrl: null,
  receivingLines: null,
  savingLines: null,
  updatingLines: null,
  filterInfo: {}
}

const actions = {
  /**
   * Fetches one or more APs with the option to pass query string filter parameters
   * @param store
   * @param opts
   * @return {Promise<*>}
   */
  async fetchApsWithQuery (store, opts) {
    let query
    let storeLines = false
    if (opts) {
      query = opts.hasOwnProperty('query') ? opts.query : opts
      storeLines = opts.hasOwnProperty('storeLines') ? opts.storeLines : false
    } else {
      query = {}
    }
    query.nest_lines = false
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.get(buildUrlWithQuery('aps', query, instanceUrl), { headers })
        .then(checkStatus)
        .then(parseJSON)
      this.commit(RESET_AP_META)
      let { results, next, previous, count } = resp
      const { result, entities } = normalize(results, apListSchema)
      if (!(result && Array.isArray(result))) {
        throw new Error('No result set in response')
      }
      let mutationPayload = { data: entities.aps }
      if (query && query.page && typeof query.page === 'number') {  // If query contains a page number
        mutationPayload.page = query.page                           // pass the page number to the mutation handler
      }
      /**
       * Assign batch data to store
       */
      const batchUniqueObjects = new Map()

      results.map(t => t.batch_data).forEach(item => { batchUniqueObjects.set(item['id'], item) })

      const batchDataResults = Array.from(batchUniqueObjects.values())
      const { entities: batchEntities, result: batchResult } = normalize(batchDataResults, batchListSchema)
      if (!batchResult) {
        return Promise.reject('Error normalizing response')
      }
      this.commit(RECEIVE_BATCHES, { data: batchEntities.batches })
      this.commit(RECEIVE_BATCH_METADATA, { next: null, previous: null, count, result: batchResult.length })

      /**
       * Assign vendor data to store
       */
      const vendorUniqueObjects = new Map()

      results.map(t => t.vendor_data).forEach(item => { vendorUniqueObjects.set(item['id'], item) })

      const vendorDataResults = Array.from(vendorUniqueObjects.values())
      const { entities: vendorEntities, result: vendorResult } = normalize(vendorDataResults, vendorListSchema)
      if (vendorEntities && vendorEntities.addresses) {
        this.commit(RECEIVE_ADDRESSES, { data: vendorEntities.addresses })
      }
      if (vendorEntities && vendorEntities.states) {
        this.commit(RECEIVE_STATES, { data: vendorEntities.addresses })
      }
      this.commit(RECEIVE_VENDORS, { data: vendorEntities.vendors })
      this.commit(RECEIVE_VENDOR_METADATA, { next, previous, count, result: vendorResult.length })

      this.commit(RECEIVE_APS, mutationPayload)
      this.commit(RECEIVE_AP_METADATA, { next, previous, count, result: result.length })
      this.commit(UPDATE_CURRENT_AP_PAGE, query && query.page || 1)
      if (storeLines) {
        const lines = await fetchLinesNew.bind(this)({ trxIds: getTrxIds(entities.aps), type: 'ap' })
        this.commit(RECEIVE_AP_LINES, { id: null, data: lines })
      }
      return { query, count, entities, results, next }
    } catch (e) {
      console.log(e)
      this.commit(RECEIVE_AP_FAILURE, handleXHRError(e))
    }
  },
  /**
   * Fetches one or more *printable* APs with the option to pass query string filter parameters
   * @param store
   * @param query
   * @return {Promise<*>}
   */
  fetchPrintableApsWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    if (query) {
      query.nest_lines = false
    } else {
      query = { nest_lines: false }
    }

    return Vue.http.get(buildUrlWithQuery('aps/printable', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (r.hasOwnProperty('results')) {
          const { results, next, previous, count } = r
          const { entities, result } = normalize(results, apListSchema)
          let mutationPayload = { data: entities.aps }
          if (query && query.page && typeof query.page === 'number') {  // If query contains a page number
            mutationPayload.page = query.page                           // pass the page number to the mutation handler
          }
          this.commit(RECEIVE_APS, mutationPayload)
          this.commit(RECEIVE_AP_METADATA, { next, previous, count, result: result.length })
          this.commit(UPDATE_CURRENT_AP_PAGE, query.page || 1)
          return { query, count, entities }
        }
      })
      .catch(e => {
        this.commit(RECEIVE_AP_FAILURE, handleXHRError(e))
      })
  },
  fetchAllAps (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.get(buildUrl('aps?serialize=false&nest_lines=false', null, instanceUrl), { headers })
      .then(checkStatus)
      .then(response => {
        if (!response || !Array.isArray(response.body)) return this.commit(RECEIVE_AP_FAILURE, 'Error normalizing response')
        this.commit(RECEIVE_ALL_APS, { data: response.body })
        return response.body
      })
      .catch(e => {
        this.commit(RECEIVE_AP_FAILURE, e.statusText)
      })
  },
  fetchAllApLines (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_AP_LINES, true)
    return Vue.http.get(buildUrl('lines/ap?serialize=false', null, instanceUrl), { headers })
      .then(checkStatus)
      .then(response => {
        this.commit(RECEIVING_AP_LINES, false)
        if (!response || !Array.isArray(response.body)) return this.commit(RECEIVE_AP_LINES_FAILURE, 'Error normalizing response')
        this.commit(RECEIVE_ALL_AP_LINES, { data: response.body })
        return response.body
      })
      .catch(e => {
        this.commit(RECEIVING_AP_LINES, false)
        this.commit(RECEIVE_AP_LINES_FAILURE, e.statusText)
      })
  },
  /**
   * Saves an AP transaction
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async saveAp (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.post(buildUrl('aps', null, instanceUrl), payload, { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (!resp) {
        throw new Error(`Unable to save AP`)
      }
      const { entities, result } = normalize(resp, apSchema)
      if (!result || (Array.isArray(result) && !result.length)) {
        throw new Error(`Error normalizing response when saving AP`)
      }
      this.commit(RECEIVE_APS, { data: entities.aps })
      if (resp && resp.lines) {
        const e = normalize(resp.lines, lineListSchema)
        this.commit(RECEIVE_AP_LINES, { id: resp.id, data: e.entities.lines })
      }
      this.commit(INCREMENT_AP_COUNT, 1)
      return resp
    } catch (e) {
      console.log(e)
      throw e
    }
  },
  /**
   * Updates an AP transaction
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async updateAp (store, payload) {
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.patch(buildUrl(`aps/${id}`, null, instanceUrl), data, { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (!resp) {
        throw new Error(`Unable to update AP with id ${id}`)
      }
      const { entities, result } = normalize(resp, apSchema)
      if (!result || (Array.isArray(result) && !result.length)) {
        throw new Error(`Error normalizing response when updating AP with id ${id}`)
      }
      this.commit(RECEIVE_APS, { data: entities.aps })
      if (resp && resp.lines) {
        const e = normalize(resp.lines, lineListSchema)
        this.commit(RECEIVE_AP_LINES, { id: resp.id, data: e.entities.lines })
      }
      return resp
    } catch (e) {
      console.log(e)
      throw e
    }
  },
  /**
   * Deletes an AP transaction
   * @param store
   * @param id
   * @return {Promise<*>}
   */
  async deleteAp (store, id) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      return await Vue.http.delete(buildUrlWithQuery(`aps/${id}`, null, instanceUrl), { headers })
        .then(checkStatus)
    } catch (e) {
      console.log(e)
      return e
    }
  },
  clearApState (store, payload = null) {
    return this.commit(CLEAR_AP_STATE, payload)
  },
  clearApTransactionLines (store, apId) {
    this.commit(CLEAR_AP_TRX_LINES, apId)
  },
  resetApMeta () {
    this.commit(RESET_AP_META)
  },
  /**
   * Fetches one or more *payment* APs with the option to pass query string filter parameters
   * @param store
   * @param query
   * @return {Promise<*>}
   */
  fetchPaymentsApsWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    if (query) {
      query.nest_lines = false
    } else {
      query = { nest_lines: false }
    }

    return Vue.http.get(buildUrlWithQuery('aps/payments', query, instanceUrl), { headers })
    .then(checkStatus)
    .then(parseJSON)
    .then(r => {
      if (r.hasOwnProperty('results')) {
        const { results, next, previous, count } = r
        const { entities, result } = normalize(results, apListSchema)
        let mutationPayload = { data: entities.aps }
        if (query && query.page && typeof query.page === 'number') {  // If query contains a page number
          mutationPayload.page = query.page                           // pass the page number to the mutation handler
        }
        this.commit(RECEIVE_APS, mutationPayload)
        this.commit(RECEIVE_AP_METADATA, { next, previous, count, result: result.length })
        this.commit(UPDATE_CURRENT_AP_PAGE, query.page || 1)
        return { query, count, entities }
      }
    })
    .catch(e => {
      this.commit(RECEIVE_AP_FAILURE, handleXHRError(e))
    })
  },

  fetchApRelatedPaymentTransactionsWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.get(buildUrlWithQuery('aps/payment_related_transactions', query, instanceUrl), { headers })
    .then(checkStatus)
    .then(parseJSON)
    .catch((e) => {
      console.log(e)
    })
  },
  setApFilterInfo (store, query) {
    this.commit(FILTER_AP_QUERY, query)
  },
  clearSetApFilterInfo (store) {
    this.commit(CLEAR_FILTER_AP_QUERY)
  }
}

const getters = {
  getApFilterInfo: (state) => {
    return state.filterInfo
  },
  getAps: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getApsCache: (state) => {
    return state.cache ? Object.values(state.cache).map(ap => Object.assign({}, ap)) : []
  },
  getApById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getAllAps: (state) => {
    return state.all || []
  },
  getApMetadata: (state) => {
    const { shown, count, next, previous } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown, next, previous }
  },
  getApByPropertyAndValue: (state) => (propObject) => {
    if (!state.data) return []
    const scanPropArray = (propName, propValuesArray) => {
      let matches = []
      for (let i = 0; i < propValuesArray.length; i++) {
        matches.push(...Object.values(state.data).filter((v) => {
          if (v[propName] === propValuesArray[i]) {
            v.transactionType = 'Accounts Payable'
            return v
          }
        }))
      }
      return matches
    }
    let ret = []
    for (let p in propObject) {
      if (!propObject.hasOwnProperty(p)) continue
      if (Array.isArray(propObject[p])) {
        ret.push(...scanPropArray(p, propObject[p]))
      } else {
        Object.values(state.data).map((v) => {
          if (!v[p]) return
          if (v[p] === propObject[p]) {
            v.transactionType = 'Accounts Payable'
            ret.push(v)
          }
        })
      }
    }
    return ret
  },
  getApLines: (state) => {
    return state.lines || {}
  },
  getAllApLines: (state) => {
    return state.allLines || []
  },
  getApLineById: (state) => (id) => {
    const { lines } = state
    for (let l in lines) {
      if (!lines.hasOwnProperty(l)) continue
      if (parseInt(lines[l].id) === parseInt(id)) return lines[l]
    }
    return {}
  },
  getApLinesByTransactionId: (state) => (id) => {
    const { lines } = state
    if (!state.lines || !Object.keys(state.lines).length) return {}
    let ret = {}
    for (let l in state.lines) {
      if (lines[l].hasOwnProperty('transaction_id') && parseInt(lines[l].transaction_id) === parseInt(id)) {
        ret[l] = lines[l]
      }
    }
    return ret
  },
  getNextApLineSet: (state) => {
    return state.nextLineUrl
  },
  getPrevApLineSet: (state) => {
    return state.prevLineUrl
  },
  getApsByNumber: (state) => (number) => {
    return Object.values(state.data).filter(v => v.number.includes(number))
  },
  getCurrentApPage: (state) => {
    if (state.currentPage) return state.currentPage
  }
}

const mutations = {
  [CLEAR_FILTER_AP_QUERY] (state) {
    state.filterInfo = {}
  },
  [FILTER_AP_QUERY] (state, query) {
    if (query.hasOwnProperty('keyword')) {
      state.filterInfo.keyword = query.keyword
    } if (query.hasOwnProperty('status__in')) {
      state.filterInfo.status__in = query.status__in
    } if (query.hasOwnProperty('batch')) {
      state.filterInfo.batch = query.batch
    } if (query.hasOwnProperty('vendor')) {
      state.filterInfo.vendor = query.vendor
    } if (query.hasOwnProperty('pageCount')) {
      state.filterInfo.pageCount = query.pageCount
    } if (query.hasOwnProperty('batch__bank__currency__in')) {
      state.filterInfo.batch__bank__currency__in = query.batch__bank__currency__in
    }
  },
  [RECEIVE_APS] (state, results) {
    for (let d in results.data) {
      results.data[d]['transactionType'] = 'Accounts Payable'
    }
    receiveData(state, results)
  },
  [RECEIVE_ALL_APS] (state, results) {
    const { data } = results
    state.all = data
    return true
  },
  [RECEIVE_AP_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [RECEIVE_AP_LINES] (state, payload) {
    const { id, data } = payload
    if (!(data && (Array.isArray(data) || Object.keys(data).length))) {
      return state.lines
    }
    let tempLineState = { ...state.lines }
    let newData = {}
    if (Array.isArray(data) && data.every(line => !!line.id)) {
      data.forEach(line => (newData[line.id] = line))
    } else {
      newData = data
    }
    if (tempLineState && Object.keys(tempLineState).length) {
      tempLineState = { ...tempLineState, ...newData }
    } else {
      tempLineState = { ...newData }
    }
    if (id) {
      let tempState = { ...state.data }
      for (let d in newData) {
        if (tempState[id] && tempState[id].hasOwnProperty('lines') && Array.isArray(tempState[id].lines)) {
          tempState[id].lines = [...new Set([...tempState[id].lines, newData[d].id])]
        }
      }
      state.data = { ...tempState }
    }
    state.lines = { ...tempLineState }
  },
  [RECEIVING_AP_LINES] (state, payload) {
    state.receivingLines = { status: payload, err: null }
  },
  [RECEIVE_ALL_AP_LINES] (state, results) {
    const { data } = results
    state.allLines = data
    return true
  },
  [RECEIVE_AP_LINES_FAILURE] (state, payload) {
    state.receivingLines = { status: false, err: payload }
  },
  [SAVING_AP_LINES] (state, payload) {
    state.savingLines = { status: payload.status, err: null }
  },
  [SAVING_AP_LINES_FAILURE] (state, payload) {
    state.savingLines = { status: false, err: payload }
  },
  [UPDATING_AP_LINES] (state, payload) {
    state.updatingLines = { status: payload, err: null }
  },
  [UPDATING_AP_LINES_FAILURE] (state, payload) {
    state.updatingLines = { status: false, err: payload }
  },
  [INCREMENT_AP_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_AP_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [RECEIVE_AP_LINES_METADATA] (state, payload) {
    receiveLinesMetadata(state, payload)
  },
  [UPDATE_CURRENT_AP_PAGE] (state, payload) {
    state.currentPage = payload
  },
  [CLEAR_AP_STATE] (state, payload) {
    let newState = {
      data: Object.assign({}),
      lines: Object.assign({}),
      saving: { status: false, err: null },
      receiveDataFailure: false,
      deleting: false
    }
    if (payload) {
      if (Array.isArray(payload) && payload.length) {
        payload.forEach(v => {
          if (newState.hasOwnProperty(v)) delete newState[v]
        })
      } else {
        newState['cache'] = Object.assign({})
      }
    }
    for (let key in newState) {
      state[key] = newState[key]
    }
  },
  [CLEAR_AP_TRX_LINES] (state, payload) {
    clearTrxLines(state, payload)
  },
  [RESET_AP_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  },
  [DELETE_AP_LINES] (state, payload) {
    deleteLine(state, payload)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/ap.js